import { Add, Delete, Edit } from "@mui/icons-material";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import { useState } from "react";
import { ConfirmacaoAcaoModal } from "../../../core/components/ConfirmacaoAcaoModal";
import { useMaquininhas } from "../../hooks/useMaquininhas";
import { useFetchMaquininhas } from "../../hooks/useFetchMaquininhas";
import { IMaquininha } from "../../interface/IMaquininha";

const MAQUININHA_VAZIA: IMaquininha = { nome: '', ativo: true };

/**
 * Cadastro das maquininhas/instituições usadas no estabelecimento. O nome escolhido aqui é o
 * que aparece no relatório de conciliação, então vale usar o mesmo rótulo do dia a dia
 * ("Stone 01", "Caixa 2"). Os campos de identificação são opcionais, mas `identificadorTerminal`
 * costuma ser a chave que consta no relatório da adquirente.
 */
export const Maquininhas = () => {
    const { maquininhas, isLoading, mutate } = useFetchMaquininhas();
    const { criar, atualizar, deletar, loading } = useMaquininhas();

    const [formularioAberto, setFormularioAberto] = useState(false);
    const [maquininha, setMaquininha] = useState<IMaquininha>(MAQUININHA_VAZIA);
    const [maquininhaExcluindo, setMaquininhaExcluindo] = useState<IMaquininha | null>(null);

    const abrirNova = () => {
        setMaquininha(MAQUININHA_VAZIA);
        setFormularioAberto(true);
    };

    const abrirEdicao = (selecionada: IMaquininha) => {
        setMaquininha(selecionada);
        setFormularioAberto(true);
    };

    const fecharFormulario = () => {
        setFormularioAberto(false);
        setMaquininha(MAQUININHA_VAZIA);
    };

    const handleSalvar = async () => {
        if (!maquininha.nome?.trim()) return;

        const resultado = maquininha.uuid
            ? await atualizar(maquininha.uuid, maquininha)
            : await criar(maquininha);

        if (resultado) {
            fecharFormulario();
            mutate();
        }
    };

    const handleExcluir = async () => {
        if (!maquininhaExcluindo?.uuid) return;

        if (await deletar(maquininhaExcluindo.uuid)) {
            setMaquininhaExcluindo(null);
            mutate();
        }
    };

    return (
        <div style={{ maxWidth: '95%', margin: '0 auto', paddingTop: '1rem' }}>
            <Paper variant="outlined" sx={{ padding: '1rem' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Maquininhas / Instituições</Typography>
                    <Button variant="contained" startIcon={<Add />} onClick={abrirNova}>
                        Nova maquininha
                    </Button>
                </Box>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>Fabricante</TableCell>
                                <TableCell>Modelo</TableCell>
                                <TableCell>Nº de série</TableCell>
                                <TableCell>Identificador do terminal</TableCell>
                                <TableCell align="center">Situação</TableCell>
                                <TableCell align="center">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center"><CircularProgress size={24} /></TableCell>
                                </TableRow>
                            )}
                            {!isLoading && !maquininhas.length && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                            Nenhuma maquininha cadastrada.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {maquininhas.map(item => (
                                <TableRow key={item.uuid}>
                                    <TableCell>{item.nome}</TableCell>
                                    <TableCell>{item.fabricante || "-"}</TableCell>
                                    <TableCell>{item.modelo || "-"}</TableCell>
                                    <TableCell>{item.numeroSerie || "-"}</TableCell>
                                    <TableCell>{item.identificadorTerminal || "-"}</TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            size="small"
                                            label={item.ativo ? "Ativa" : "Inativa"}
                                            color={item.ativo ? "success" : "default"}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Button size="small" startIcon={<Edit />} onClick={() => abrirEdicao(item)}>
                                            Editar
                                        </Button>
                                        <Button size="small" color="error" startIcon={<Delete />} onClick={() => setMaquininhaExcluindo(item)}>
                                            Excluir
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={formularioAberto} onClose={fecharFormulario} maxWidth="xs" fullWidth>
                <DialogTitle>{maquininha.uuid ? "Editar maquininha" : "Nova maquininha"}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField
                            size="small"
                            label="Nome"
                            fullWidth
                            required
                            helperText="É este nome que aparece no relatório de conciliação"
                            value={maquininha.nome ?? ''}
                            onChange={(e) => setMaquininha(prev => ({ ...prev, nome: e.target.value }))}
                            inputProps={{ maxLength: 120 }}
                        />
                        <TextField
                            size="small"
                            label="Fabricante"
                            fullWidth
                            placeholder="Stone, Cielo, Getnet..."
                            value={maquininha.fabricante ?? ''}
                            onChange={(e) => setMaquininha(prev => ({ ...prev, fabricante: e.target.value }))}
                        />
                        <TextField
                            size="small"
                            label="Modelo"
                            fullWidth
                            value={maquininha.modelo ?? ''}
                            onChange={(e) => setMaquininha(prev => ({ ...prev, modelo: e.target.value }))}
                        />
                        <TextField
                            size="small"
                            label="Nº de série"
                            fullWidth
                            value={maquininha.numeroSerie ?? ''}
                            onChange={(e) => setMaquininha(prev => ({ ...prev, numeroSerie: e.target.value }))}
                        />
                        <TextField
                            size="small"
                            label="Identificador do terminal"
                            fullWidth
                            helperText="Código do terminal na adquirente, quando houver"
                            value={maquininha.identificadorTerminal ?? ''}
                            onChange={(e) => setMaquininha(prev => ({ ...prev, identificadorTerminal: e.target.value }))}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={maquininha.ativo ?? true}
                                    onChange={(e) => setMaquininha(prev => ({ ...prev, ativo: e.target.checked }))}
                                    color="success"
                                />
                            }
                            label="Ativa"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={fecharFormulario}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSalvar} disabled={!maquininha.nome?.trim() || loading}>
                        {loading ? <CircularProgress size={20} /> : "Salvar"}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmacaoAcaoModal
                open={!!maquininhaExcluindo}
                titulo="Excluir maquininha"
                descricao={`Deseja excluir "${maquininhaExcluindo?.nome}"? Os pagamentos já registrados com ela continuam intactos no relatório.`}
                onConfirmar={handleExcluir}
                onCancelar={() => setMaquininhaExcluindo(null)}
                loading={loading}
            />
        </div>
    );
};
